import { App } from '@capacitor/app';
import { isAndroid, isNative } from './platform.js';
import { currentRoute, navigate, toast } from '../runtime/legacy.js';

/**
 * Hardware back button handling for Android.
 *
 * Google Play review treats a back button that closes the app from a detail
 * screen as a defect, and a back button that silently discards an in-progress
 * workout as a worse one. The rules here:
 *
 *   1. If a modal is open, close the modal.
 *   2. If we are on a nested route, go to that section's root.
 *   3. If we are on a root tab other than the dashboard, go to the dashboard.
 *   4. On the dashboard, require a second press within 2s to exit.
 *
 * An active workout overrides all of it — back asks for confirmation first.
 */

const ROOT_ROUTES = new Set([
  '/dashboard',
  '/workouts',
  '/exercises',
  '/social',
  '/missions',
  '/shop',
  '/stats',
  '/profile',
  '/support',
  '/subscription',
  '/plans/generate',
]);

const HOME_ROUTE = '/dashboard';
const EXIT_CONFIRM_WINDOW_MS = 2000;

let lastBackPress = 0;

function closeTopmostModal() {
  // The legacy core renders modals as `.modal-overlay` siblings on the body and
  // closes them by removal, so removing the last one is the equivalent action.
  const overlays = document.querySelectorAll('.modal-overlay, .modal-backdrop');
  const top = overlays[overlays.length - 1];
  if (!top) return false;

  const closeButton = top.querySelector('[data-close], .modal-close');
  if (closeButton) closeButton.click();
  else top.remove();
  return true;
}

/** '/social/pact/123' -> '/social'; returns null when already at a root. */
function parentRoute(route) {
  if (ROOT_ROUTES.has(route)) return null;
  const segments = route.split('/').filter(Boolean);
  if (segments.length <= 1) return null;

  const parent = `/${segments[0]}`;
  return ROOT_ROUTES.has(parent) ? parent : HOME_ROUTE;
}

function handleBack(canGoBackInWebView) {
  if (closeTopmostModal()) return;

  const route = currentRoute();

  if (globalThis.AppState?.activeWorkout && route.startsWith('/workout')) {
    toast('Finish or cancel your workout before leaving', 'info');
    return;
  }

  const parent = parentRoute(route);
  if (parent) {
    navigate(parent);
    return;
  }

  if (route !== HOME_ROUTE && route !== '/') {
    navigate(HOME_ROUTE);
    return;
  }

  // At the top of the stack. Prefer the web view's own history if it has any,
  // otherwise require a deliberate double-press to leave the app.
  if (canGoBackInWebView && window.history.length > 1) {
    window.history.back();
    return;
  }

  const now = Date.now();
  if (now - lastBackPress < EXIT_CONFIRM_WINDOW_MS) {
    App.exitApp();
  } else {
    lastBackPress = now;
    toast('Press back again to exit', 'info', EXIT_CONFIRM_WINDOW_MS);
  }
}

export function installBackButton() {
  if (!isNative || !isAndroid) return;
  App.addListener('backButton', ({ canGoBack }) => handleBack(canGoBack));
}
